import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BACKEND_URL } from '../config';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  ArrowLeft, ShieldCheck, Download, Search, Check, Minus, AlertTriangle, Info,
} from 'lucide-react';
import { toast } from 'sonner';

const API = BACKEND_URL;

const PermissionsMatrix = ({ user }) => {
  const navigate = useNavigate();
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [downloading, setDownloading] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchMatrix(); }, []);

  const fetchMatrix = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/rbac/matrix`, { headers });
      setMatrix(res.data);
    } catch (err) {
      toast.error('Failed to load permissions matrix');
    } finally {
      setLoading(false);
    }
  };

  const downloadExport = async (format) => {
    setDownloading(format);
    try {
      const res = await axios.get(`${API}/api/rbac/matrix/export.${format}`, {
        headers, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.setAttribute('download', `ISISP_RBAC_Matrix_${today}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Permissions matrix downloaded (.${format})`);
    } catch (err) {
      toast.error('Download failed — administrator access required');
    } finally {
      setDownloading(false);
    }
  };

  if (loading || !matrix) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const { roles, modules } = matrix;
  const groups = Array.from(new Set(modules.map((m) => m.group)));
  const q = query.trim().toLowerCase();
  const filtered = modules.filter((m) =>
    !q || m.name.toLowerCase().includes(q) || m.path.toLowerCase().includes(q) || m.group.toLowerCase().includes(q)
  );

  // Role coverage stats
  const coverageByRole = roles.map((r) => ({
    ...r,
    count: modules.filter((m) => m.allowed_roles.includes(r.id)).length,
  }));

  const groupBadgeColor = (group) => {
    switch (group) {
      case 'Core':           return 'bg-slate-100 text-slate-700';
      case 'Registries':     return 'bg-indigo-100 text-indigo-700';
      case 'Operational':    return 'bg-emerald-100 text-emerald-700';
      case 'Administration': return 'bg-rose-100 text-rose-700';
      default:               return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Dashboard
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ShieldCheck className="h-7 w-7 text-blue-600" /> Permissions Matrix
            </h1>
            <p className="text-gray-600 mt-1">
              Canonical role-to-module access map. This page reflects the live configuration enforced by the application.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => downloadExport('pdf')}
            disabled={!!downloading}
            variant="outline"
            className="border-blue-600 text-blue-700 hover:bg-blue-50"
            data-testid="download-pdf-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            {downloading === 'pdf' ? 'Generating…' : 'Download .pdf'}
          </Button>
          <Button
            onClick={() => downloadExport('docx')}
            disabled={!!downloading}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="download-docx-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            {downloading === 'docx' ? 'Generating…' : 'Download .docx'}
          </Button>
        </div>
      </div>

      {/* Coverage cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {coverageByRole.map((r) => (
          <Card key={r.id} data-testid={`coverage-${r.id}`}>
            <CardContent className="p-4">
              <p className="text-xs text-gray-500">{r.label}</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{r.count}</p>
              <p className="text-xs text-gray-500 mt-0.5">of {modules.length} modules</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search + Legend */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            placeholder="Search modules, paths, groups…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
            data-testid="search-input"
          />
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-600">
          <span className="flex items-center gap-1"><Check className="h-3.5 w-3.5 text-green-700" /> Allowed</span>
          <span className="flex items-center gap-1"><Minus className="h-3.5 w-3.5 text-gray-300" /> Not allowed</span>
        </div>
      </div>

      {/* Group tables */}
      {groups.map((group) => {
        const rows = filtered.filter((m) => m.group === group);
        if (rows.length === 0) return null;
        return (
          <Card key={group}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Badge className={groupBadgeColor(group)}>{group}</Badge>
                <span className="text-gray-500 text-sm font-normal">({rows.length} {rows.length === 1 ? 'module' : 'modules'})</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid={`group-table-${group.toLowerCase()}`}>
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-gray-500 border-b">
                      <th className="py-2 pr-4">Module</th>
                      <th className="py-2 pr-4">Path</th>
                      {roles.map((r) => (
                        <th key={r.id} className="py-2 px-2 text-center whitespace-nowrap">{r.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((m) => (
                      <tr key={m.path} className="border-b last:border-0 hover:bg-gray-50" data-testid={`row-${m.path}`}>
                        <td className="py-3 pr-4 align-top">
                          <p className="font-medium text-gray-900">{m.name}</p>
                          <p className="text-xs text-gray-500 mt-0.5 max-w-md">{m.description}</p>
                        </td>
                        <td className="py-3 pr-4 align-top">
                          <code className="text-xs text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded">{m.path}</code>
                        </td>
                        {roles.map((r) => (
                          <td key={r.id} className="py-3 px-2 text-center align-top">
                            {m.allowed_roles.includes(r.id) ? (
                              <Check className="h-4 w-4 text-green-700 mx-auto" />
                            ) : (
                              <Minus className="h-4 w-4 text-gray-300 mx-auto" />
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Footer note */}
      <Card className="border-amber-200 bg-amber-50/40">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <p className="font-medium">How enforcement works</p>
            <ul className="list-disc list-inside mt-1 space-y-0.5 text-amber-800">
              <li>
                <strong>Frontend:</strong> every protected route is wrapped in <code className="bg-amber-100 px-1 rounded">ProtectedModuleRoute</code>;
                unauthorised users are redirected to <code className="bg-amber-100 px-1 rounded">/dashboard</code>.
              </li>
              <li>
                <strong>Backend:</strong> sensitive endpoints check the role of the authenticated user before performing
                writes (e.g. score overrides, re-certification, user management).
              </li>
              <li>
                <strong>Source of truth:</strong> <code className="bg-amber-100 px-1 rounded">backend/services/rbac_matrix.py</code>.
                The frontend mirror at <code className="bg-amber-100 px-1 rounded">frontend/src/lib/rbacMatrix.js</code> is loaded
                synchronously at boot so navigation does not require an extra request.
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PermissionsMatrix;
